import type { CreateTask, Task } from '../db/models'

/**
 * Copy every user-visible field from `source` into a fresh `CreateTask`
 * input with a new UUID. Callers pass `overrides` for anything the clone
 * should differ on (usually `order`, `sectionId`, or status resets).
 *
 * Keeping this in one place means a new Task field only needs to be added
 * here once — before, every duplicate call-site had its own hand-written
 * copy block that silently drifted when new fields were introduced.
 *
 * `tags` is cloned into a new array so later mutations on the source
 * don't leak into the duplicate.
 */
export function cloneTaskFields(
  source: Task,
  overrides?: Partial<CreateTask>,
): CreateTask & { id: string } {
  return {
    id: crypto.randomUUID(),
    title: source.title,
    description: source.description,
    status: source.status,
    attention: source.attention,
    category: source.category,
    lifeSpace: source.lifeSpace,
    projectId: source.projectId,
    sectionId: source.sectionId,
    dueDate: source.dueDate,
    dueTime: source.dueTime,
    duePeriod: source.duePeriod,
    startDate: source.startDate,
    startTime: source.startTime,
    startPeriod: source.startPeriod,
    completedAt: source.completedAt,
    estimatedDuration: source.estimatedDuration,
    actualDuration: source.actualDuration,
    durationMode: source.durationMode,
    tags: [...source.tags],
    rewardId: source.rewardId,
    /*
      Die Kopie erbt die gezogene Position im Tag (Abnahme F-DN1) — und landet
      damit direkt NEBEN dem Original, nicht ans Tagesende: Bei gleichem
      `dayOrder` entscheidet `order`, und den bekommt die Kopie höher.
      Ein Original, das nie gezogen wurde (`null`), vererbt `null`; dann
      entscheiden wie bisher Uhrzeit und `order`.
    */
    dayOrder: source.dayOrder,
    parentTaskId: source.parentTaskId,
    pausedAt: source.pausedAt,
    recurrence: source.recurrence,
    /*
      Eine Kopie ist keine Todoist-Aufgabe — auch nicht der Nachfolger einer
      Wiederholung, der hier durchläuft. Die Herkunft bleibt am Original, damit
      ein zweiter Import es findet und nichts doppelt anlegt.
    */
    importedFrom: null,
    // Eine Kopie ist eine neue Aufgabe, kein Favorit (#210). Die nächste Runde einer Wiederholung
    // bekommt den Stern ausdrücklich über `overrides` (`spawnNextOccurrence`).
    favoriteOrder: null,
    // Eine Kopie ist eine zweite Sache und beginnt bei null (#198); der Nachfolger bekommt die
    // Zähler über `overrides`.
    completedCount: null,
    postponedCount: null,
    ...overrides,
  }
}
